/**
 * Data Quality API Type Definitions
 * Based on DATA_QUALITY_API_INTEGRATION_GUIDE.md
 */

// ============================================================================
// Enums
// ============================================================================

export enum TimeWindow {
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
}

export enum BenchmarkStatus {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  ACCEPTABLE = 'ACCEPTABLE',
  NEEDS_IMPROVEMENT = 'NEEDS_IMPROVEMENT',
  CRITICAL = 'CRITICAL',
}

export enum MetricBenchmark {
  ABOVE_TARGET = 'ABOVE_TARGET',
  AT_TARGET = 'AT_TARGET',
  BELOW_TARGET = 'BELOW_TARGET',
  CRITICAL = 'CRITICAL',
}

export enum TrendDirection {
  IMPROVING = 'IMPROVING',
  STABLE = 'STABLE',
  DECLINING = 'DECLINING',
}

export enum ComplianceStatus {
  COMPLIANT = 'COMPLIANT',
  WARNING = 'WARNING',
  BREACHED = 'BREACHED',
}

export enum ErrorCategory {
  MISSING_VALUES = 'MISSING_VALUES',
  DUPLICATES = 'DUPLICATES',
  CONFLICTS = 'CONFLICTS',
  FORMAT_ISSUES = 'FORMAT_ISSUES',
  RANGE_VIOLATIONS = 'RANGE_VIOLATIONS',
}

export enum MetricType {
  TRUST_SCORE = 'trust_score',
  COMPLETENESS_SCORE = 'completeness_score',
  FRESHNESS_SCORE = 'freshness_score',
  QUALITY_RULES_SCORE = 'quality_rules_score',
  SCHEMA_STABILITY_SCORE = 'schema_stability_score',
  PIPELINE_SUCCESS_SCORE = 'pipeline_success_score',
  SLA_HEALTH_SCORE = 'sla_health_score',
  SOURCE_RELIABILITY_SCORE = 'source_reliability_score',
}

// ============================================================================
// Shared Interfaces
// ============================================================================

export interface MetricScore {
  current_value: number
  benchmark: MetricBenchmark
  target_value: number
  trend: TrendDirection
}

export interface KPIBreakdown {
  completeness_score: MetricScore
  freshness_hours: MetricScore
  quality_rules_pass_rate: MetricScore
  schema_stability_score: MetricScore
  pipeline_success_rate: MetricScore
  sla_health_index: MetricScore
  source_reliability_score: MetricScore
}

// ============================================================================
// API Response Wrapper
// ============================================================================

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  metadata?: {
    timestamp: string
    user_id: string
  }
}

// ============================================================================
// API Response Types
// ============================================================================

export interface AggregateTrustScoreData {
  overall_trust_score: number
  benchmark_status: BenchmarkStatus
  time_window: string
  period_start: string
  period_end: string
  kpi_breakdown: {
    completeness_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    freshness_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    quality_rules_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    schema_stability_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    pipeline_success_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    sla_health_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
    source_reliability_score: {
      value: number
      benchmark_status: string
      last_calculated: string
      sample_count: number
    }
  }
  total_sources: number
  sources_above_threshold: number
  quality_threshold: number
}

export type AggregateTrustScoreResponse = ApiResponse<AggregateTrustScoreData>

export interface SourceMetricsData {
  data_source_id: number
  data_source_name: string
  time_window: string
  period_start: string
  period_end: string
  trust_score: {
    value: number
    benchmark_status: string
    last_calculated: string
  }
  kpi_scores: {
    completeness_score: { value: number; benchmark_status: string }
    freshness_score: { value: number; benchmark_status: string }
    quality_rules_score: { value: number; benchmark_status: string }
    schema_stability_score: { value: number; benchmark_status: string }
    pipeline_success_score: { value: number; benchmark_status: string }
    sla_health_score: { value: number; benchmark_status: string }
    source_reliability_score: { value: number; benchmark_status: string }
  }
  recent_processing_jobs: Array<{
    job_id: string
    status: string
    started_at: string
    completed_at: string
    records_processed: number
    quality_score: number
  }>
  last_successful_update: string
  total_records_processed: number
}

export type SourceMetricsResponse = ApiResponse<SourceMetricsData>

export interface AffectedSource {
  source_id: number
  source_name: string
  error_count: number
}

export interface ErrorCategoryDetail {
  category: string
  count: number
  percentage: number
  trend: TrendDirection
  top_affected_sources: AffectedSource[]
}

export interface ErrorAnalysisData {
  time_window: string
  total_errors: number
  error_rate_percentage: number
  error_categories: ErrorCategoryDetail[]
  recommendations: string[]
}

export type ErrorAnalysisResponse = ApiResponse<ErrorAnalysisData>

export interface SLADetail {
  sla_name: string
  data_source_id: number
  data_source_name: string
  target_value: number
  current_value: number
  compliance_status: ComplianceStatus
  last_breach_date: string | null
  total_breaches: number
  breaches_last_30_days: number
}

export interface SLASummary {
  affected_sources: number
  total_breaches: number
  critical_breaches: number
  high_breaches: number
  medium_breaches: number
  low_breaches: number
  overall_compliance_rate: number
}

export interface SLATypeMetadata {
  type: string
  label: string
  description: string
}

export interface SLABreach {
  data_source_id: number
  data_source_name: string
  metric_name: string
  sla_type: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  actual_value: number
  target_value: number
  breach_count: number
  detected_at: string
  last_updated: string | null
  breach_details: string
}

export interface SLAStatusData {
  summary: SLASummary
  recent_breaches: SLABreach[]
  available_sla_types: SLATypeMetadata[]
  data_source_id?: number
  severity_filter?: string
  time_window: string
}

export type SLAStatusResponse = ApiResponse<SLAStatusData>

export interface SourceBreakdown {
  source_id: number
  source_name: string
  value: number
}

export interface DataPoint {
  date: string
  value: number
  source_breakdown?: SourceBreakdown[]
}

export interface StatisticalSummary {
  average: number
  median: number
  min: number
  max: number
  std_deviation: number
  trend_direction: TrendDirection
}

export interface HistoricalTrendsData {
  metric_type: string
  days_back: number
  data_points: DataPoint[]
  statistical_summary: StatisticalSummary
}

export type HistoricalTrendsResponse = ApiResponse<HistoricalTrendsData>

export interface AlertRelatedMetrics {
  source_name?: string
  metric_name?: string
}

export interface Alert {
  id: number | string
  alert_title: string
  alert_type: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  related_metrics?: AlertRelatedMetrics
}

export interface ActiveAlertsSummary {
  active_alerts: number
  critical_alerts: number
  high_alerts: number
}

export interface ActiveAlertsData {
  alerts: Alert[]
  summary: ActiveAlertsSummary
}

export type ActiveAlertsResponse = ApiResponse<ActiveAlertsData>

export interface ActiveAlertsParams {
  data_source_id?: number
  severity?: string
  alert_type?: string
  limit?: number
  offset?: number
}

// ============================================================================
// API Request Types
// ============================================================================

export interface SLAThreshold {
  sla_name: string
  target_value: number
  warning_threshold?: number
}

export interface SLAConfigurationRequest {
  data_source_id: number
  sla_thresholds: SLAThreshold[]
}

export interface SLAThresholdDetail extends SLAThreshold {
  status: 'CREATED' | 'UPDATED'
}

export interface SLAConfigurationResponse {
  message: string
  data_source_id: number
  configured_slas: number
  sla_details: SLAThresholdDetail[]
}

// ============================================================================
// Query Parameters Types
// ============================================================================

export interface AggregateTrustScoreParams {
  time_window?: TimeWindow
  quality_threshold?: number
}

export interface SourceMetricsParams {
  time_window?: TimeWindow
}

export interface ErrorAnalysisParams {
  time_window?: TimeWindow
  error_type?: ErrorCategory
}

export interface SLAStatusParams {
  data_source_id?: number
  severity?: ComplianceStatus
}

export interface HistoricalTrendsParams {
  metric_type: MetricType
  days_back?: number
}

export interface DashboardSummaryParams {
  time_window?: TimeWindow
  comparison_days?: number
}

export interface PipelineHealthParams {
  processing_job_id?: number
  data_source_id?: number
  time_window?: TimeWindow
}

export interface SchemaDriftParams {
  time_window?: 'week' | 'month' | 'quarter' | 'year'
}

export interface SchemaStatusSummaryParams {
  data_source_ids?: string
}

export interface SourceMetricsWithTrendsParams {
  comparison_days?: number
  include_detailed_trends?: boolean
  profile?: 'minimal' | 'standard' | 'detailed' | 'complete'
  error_time_series_days?: number
}

export interface TimeSeriesTrendParams {
  metric_type: MetricType
  data_source_id?: number
  days_back?: number
  granularity?: 'hourly' | 'daily' | 'weekly'
  include_trend_line?: boolean
}

export interface SourceReliabilityTableParams {
  time_window?: TimeWindow
  limit?: number
}

// ============================================================================
// Additional Response Types
// ============================================================================

export interface DashboardSummaryData {
  overall_metrics: {
    trust_score: number
    trend_percentage: number
    trend_direction: 'up' | 'down' | 'stable'
  }
  kpi_summaries: Array<{
    metric_name: string
    current_value: number
    previous_value: number
    trend_percentage: number
    benchmark_status: string
  }>
  source_summaries: Array<{
    source_id: number
    source_name: string
    trust_score: number
    trend_percentage: number
    status: 'reliable' | 'monitor' | 'unreliable'
  }>
}

export type DashboardSummaryResponse = ApiResponse<DashboardSummaryData>

export interface PipelineHealthData {
  end_to_end_pipeline_success: {
    description: string
    stages: Array<{
      name: 'Extract' | 'Transform' | 'Validate' | 'Load' | 'Index'
      duration_seconds: number
      status: 'success' | 'warning' | 'error'
      description: string
    }>
    overall_success_rate: number
  }
  success_rate_breakdown: {
    successful: { percentage: number; label: string }
    partial: { percentage: number; label: string }
    failed: { percentage: number; label: string }
  }
  error_type_tracking: {
    description: string
    total_errors: number
    categories: Array<{
      name: string
      count: number
      percentage: number
      color: string
    }>
    target_error_rate: string
  }
  metadata: {
    processing_job_id?: number
    data_source_id?: number
    time_window: string
    calculation_timestamp: string
  }
}

export type PipelineHealthResponse = ApiResponse<PipelineHealthData>

export interface SchemaDriftData {
  data_source_id: number
  source_name: string
  time_window: string
  drift_metrics: {
    changes_per_week: number
    drift_status: 'stable' | 'moderate' | 'active' | 'volatile'
    stability_score: number
    total_changes: number
    volatility_analysis: {
      trend_direction: 'increasing' | 'decreasing' | 'stable'
      recent_spike_detected: boolean
    }
  }
  recent_activity: Array<{
    date: string
    changes: number
    types: string
  }>
  recommendations: string[]
}

export type SchemaDriftResponse = ApiResponse<SchemaDriftData>

export interface SchemaStatusSummaryData {
  total_sources_analyzed: number
  overall_health: string
  status_breakdown: {
    stable: number
    moderate: number
    active: number
    volatile: number
  }
  average_metrics: {
    avg_stability_score: number
    avg_changes_per_week: number
  }
  top_volatile_sources: Array<{
    source_id: number
    source_name: string
    changes_per_week: number
    drift_status: string
  }>
  most_stable_sources: Array<{
    source_id: number
    source_name: string
    changes_per_week: number
    stability_score: number
  }>
}

export type SchemaStatusSummaryResponse = ApiResponse<SchemaStatusSummaryData>

interface MetricWithTrend {
  current_value: number
  trend: {
    percentage_change: number
    trend_direction: 'up' | 'down' | 'stable'
    trend_significance: string
    current_period_avg: number
    previous_period_avg: number
    comparison_days: number
    data_points_current: number
    data_points_previous: number
    trend_label: string
  }
  benchmark_status: string
}

export interface SourceMetricsWithTrendsData {
  data_source_id: number
  source_name: string
  category_name: string
  has_data: boolean
  calculation_timestamp: string
  metrics: {
    trust_score: MetricWithTrend
    completeness_score: MetricWithTrend
    quality_rules_pass_rate: MetricWithTrend
    pipeline_success_rate: MetricWithTrend
    sla_health_score: MetricWithTrend
    source_reliability_score: MetricWithTrend
  }
  summary: {
    total_records_processed: number
    pipeline_execution_time_ms: number
    freshness_hours: number
    freshness_percentage: number
    duplicate_percentage: number
    comparison_period: string
  }
  schema_drift: {
    drift_status: string
    stability_score: number
    changes_per_week: number
    total_changes: number
    volatility: number
    enhanced_classifications: {
      risk_level: string
      action_required: string
      business_impact: string
      data_source_type: string
      classification_summary: {
        overall_health: string
        attention_priority: string
        operational_impact: string
      }
    }
    trend_direction: string
    change_pattern: string
  }
  combined_health_assessment: {
    overall_health: string
    quality_health: string
    schema_health: string
    attention_required: string
    risk_level: string
  }
  error_breakdown?: {
    from_latest_file: boolean
    latest_file_name: string
    latest_upload_date: string
    errors_by_type: {
      missing_values: number
      duplicates: number
      format_issues: number
      range_violations: number
      conflicts: number
    }
    schema_drift_detail: {
      fields_added: number
      fields_removed: number
      fields_modified: number
      type_changes: number
    }
  }
  used_by?: {
    total_consumers: number
    consumers: Array<{
      name: string
      type: 'AI_AGENT' | 'MODEL' | 'API' | 'SYSTEM'
      last_accessed: string
      access_count: number
    }>
  }
  schema_overview?: {
    total_fields: number
    fields: Array<{
      field_name: string
      data_type: string
      is_required: boolean
      canonical_field: string
      validation_rules_count: number
    }>
  }
  latest_file?: {
    file_id: number
    original_filename: string
    file_type: string
    file_size_bytes: number
    upload_date: string
    upload_status: string
    protocol: string
  }
  file_health?: {
    status: 'HEALTHY' | 'WARNING' | 'CRITICAL'
    classification_criteria: {
      error_rate: number
      completeness: number
      schema_stability: number
    }
    recommendation: string
  }
  file_sla_status?: {
    status: 'ON_TIME' | 'AT_RISK' | 'BREACHED' | 'NO_SLA_CONFIGURED'
    time_since_last_upload_hours: number
    expected_frequency_hours: number
    next_expected_upload: string
    compliance_percentage: number
  }
  source_impact?: {
    level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
    impact_factors: {
      consumer_count: number
      data_source_priority: number
      avg_records_per_file: number
      downstream_dependencies: number
    }
    reasoning: string
  }
  error_rate_time_series?: {
    period_start: string
    period_end: string
    data_points: Array<{
      date: string
      total_records: number
      error_count: number
      error_rate: number
      primary_error_type: string
    }>
    average_error_rate: number
    trend_direction: 'increasing' | 'decreasing' | 'stable' | 'insufficient_data'
  }
}

export type SourceMetricsWithTrendsResponse =
  ApiResponse<SourceMetricsWithTrendsData>

export interface TimeSeriesTrendData {
  metric_type: string
  data_source_id?: number
  days_back: number
  granularity: string
  period_start: string
  period_end: string
  chart_data: Array<{
    date: string
    value: number
    min_value: number
    max_value: number
    data_points: number
    benchmark_status: string
    moving_average?: number
  }>
  trend_statistics: {
    trend_percentage: number
    trend_direction: 'up' | 'down' | 'stable'
    recent_average: number
    historical_average: number
    volatility: number
    data_quality: 'high' | 'medium' | 'low'
  }
  trend_line: Array<{
    date: string
    trend_value: number
  }>
  summary: {
    total_data_points: number
    best_value: number
    worst_value: number
    average_value: number
  }
}

export type TimeSeriesTrendResponse = ApiResponse<TimeSeriesTrendData>

export interface SourceReliabilityTableData {
  sources: Array<{
    source_id: number
    source_name: string
    completeness: number
    timeliness: number
    quality: number
    schema: number
    duplicates: number
    score: number
    status: 'reliable' | 'monitor' | 'unreliable' | 'no_data' | 'error'
  }>
  summary: {
    total_sources: number
    avg_completeness: number
    avg_timeliness: number
    avg_quality: number
    avg_schema: number
    avg_duplicates: number
    avg_score: number
  }
  status_breakdown: {
    reliable: number
    monitor: number
    unreliable: number
    no_data: number
    error: number
  }
  color_coding: {
    reliable: { threshold: string; color: string; label: string }
    monitor: { threshold: string; color: string; label: string }
    unreliable: { threshold: string; color: string; label: string }
  }
}

export type SourceReliabilityTableResponse =
  ApiResponse<SourceReliabilityTableData>

// ============================================================================
// Error Response Type
// ============================================================================

export interface DataQualityErrorResponse {
  detail: string
  error_code?: string
  correlation_id: string
  timestamp: string
}
